<template>
  <main class="signups-dashboard">
    <header class="page-head">
      <h1>Signups Overview <small class="admin-note">(Admin/Mod)</small></h1>
      <p class="sub">Daily and monthly trends across pending, approved, and rejected signups.</p>
    </header>

    <!-- KPIs -->
    <section class="grid kpis">
      <article class="kpi">
        <h3>Pending (now)</h3>
        <div class="kpi__value">{{ totals.pending.toLocaleString() }}</div>
        <p class="kpi__hint">Currently awaiting review</p>
      </article>
      <article class="kpi">
        <h3>Approved ({{ dailyRange.days }}d)</h3>
        <div class="kpi__value">{{ metrics.range_approved.toLocaleString() }}</div>
        <p class="kpi__hint">Approved in this window</p>
      </article>
      <article class="kpi">
        <h3>Rejected ({{ dailyRange.days }}d)</h3>
        <div class="kpi__value">{{ metrics.range_rejected.toLocaleString() }}</div>
        <p class="kpi__hint">Rejected in this window</p>
      </article>
      <article class="kpi">
        <h3>Approval Rate</h3>
        <div class="kpi__value">{{ metrics.approval_rate_pct }}%</div>
        <p class="kpi__hint">Approved / (Approved + Rejected)</p>
      </article>
    </section>

    <!-- Daily chart card -->
    <section class="card">
      <header class="card__head">
        <h2>Daily signups</h2>
        <div class="controls">
          <label>
            Range:
            <select v-model.number="days" @change="fetchDaily">
              <option :value="7">7 days</option>
              <option :value="30">30 days</option>
              <option :value="90">90 days</option>
            </select>
          </label>
          <div class="legend">
            <span class="lg lg--approved">Approved</span>
            <span class="lg lg--rejected">Rejected</span>
            <span class="lg lg--pending">Pending</span>
          </div>
        </div>
      </header>

      <div v-if="loadingDaily" class="skeleton">
        <div class="skeleton-row" v-for="n in 5" :key="'d'+n"></div>
      </div>
      <div v-else class="chart-wrap">
        <svg :viewBox="`0 0 ${vw} ${vh}`" class="chart">
          <!-- axes -->
          <line :x1="pad" :y1="vh-pad" :x2="vw-pad" :y2="vh-pad" class="axis"/>
          <line :x1="pad" :y1="pad"   :x2="pad"     :y2="vh-pad" class="axis"/>

          <!-- grid y -->
          <g v-for="(y,k) in yTicks" :key="'gy'+k">
            <line :x1="pad" :x2="vw-pad" :y1="y" :y2="y" class="grid"/>
          </g>

          <!-- series -->
          <polyline
            v-if="dailySeries.approved.points.length"
            :points="asPointString(dailySeries.approved.points)"
            class="line line--approved"
            fill="none"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <polyline
            v-if="dailySeries.rejected.points.length"
            :points="asPointString(dailySeries.rejected.points)"
            class="line line--rejected"
            fill="none"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <polyline
            v-if="dailySeries.pending.points.length"
            :points="asPointString(dailySeries.pending.points)"
            class="line line--pending"
            fill="none"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />

          <!-- x labels (sparse) -->
          <g v-for="(tick, i) in xTicksDaily" :key="'dx'+i">
            <text :x="tick.x" :y="vh - pad + 16" class="tick" text-anchor="middle">
              {{ tick.label }}
            </text>
          </g>

          <!-- y labels -->
          <g v-for="(val, i) in yTickVals" :key="'dy'+i">
            <text :x="pad - 6" :y="val.y" class="tick" text-anchor="end" dominant-baseline="middle">
              {{ val.label }}
            </text>
          </g>
        </svg>
      </div>
    </section>

    <!-- Monthly chart card -->
    <section class="card">
      <header class="card__head">
        <h2>Monthly signups</h2>
        <div class="controls">
          <label>
            Range:
            <select v-model.number="months" @change="fetchMonthly">
              <option :value="6">6 months</option>
              <option :value="12">12 months</option>
              <option :value="24">24 months</option>
            </select>
          </label>
          <div class="legend">
            <span class="lg lg--approved">Approved</span>
            <span class="lg lg--rejected">Rejected</span>
            <span class="lg lg--pending">Pending</span>
          </div>
        </div>
      </header>

      <div v-if="loadingMonthly" class="skeleton">
        <div class="skeleton-row" v-for="n in 5" :key="'m'+n"></div>
      </div>
      <div v-else class="chart-wrap">
        <svg :viewBox="`0 0 ${vw} ${vh}`" class="chart">
          <!-- axes -->
          <line :x1="pad" :y1="vh-pad" :x2="vw-pad" :y2="vh-pad" class="axis"/>
          <line :x1="pad" :y1="pad"   :x2="pad"     :y2="vh-pad" class="axis"/>

          <!-- grid y -->
          <g v-for="(y,k) in yTicksMonthly" :key="'gmy'+k">
            <line :x1="pad" :x2="vw-pad" :y1="y" :y2="y" class="grid"/>
          </g>

          <!-- grouped bars per month: pending/approved/rejected -->
          <g v-for="(bucket, i) in monthlyBuckets" :key="'bm'+i">
            <g :transform="`translate(${bucket.x - barGroupWidth/2},0)`">
              <rect
                v-for="b in bucket.bars"
                :key="b.key"
                :x="b.offset"
                :y="b.y"
                :width="barWidth"
                :height="b.h"
                :class="b.cls"
                rx="4" ry="4"
              />
            </g>
            <!-- x label -->
            <text :x="bucket.x" :y="vh - pad + 16" class="tick" text-anchor="middle">
              {{ bucket.label }}
            </text>
          </g>

          <!-- y labels -->
          <g v-for="(val, i) in yTickValsMonthly" :key="'my'+i">
            <text :x="pad - 6" :y="val.y" class="tick" text-anchor="end" dominant-baseline="middle">
              {{ val.label }}
            </text>
          </g>
        </svg>
      </div>
    </section>

    <!-- Engagement -->
<section class="card">
  <header class="card__head">
    <h2>User engagement</h2>
    <div class="controls">
      <label>
        Daily range:
        <select v-model.number="pvDays" @change="fetchEngagement">
          <option :value="7">7 days</option>
          <option :value="30">30 days</option>
          <option :value="90">90 days</option>
        </select>
      </label>
      <label>
        Top lists:
        <select v-model.number="topDays" @change="fetchEngagement">
          <option :value="7">7 days</option>
          <option :value="30">30 days</option>
        </select>
      </label>
      <div class="legend">
        <span class="lg" style="--c: #0ea5e9"><span class="sw" /></span><span>Pageviews</span>
        <span class="lg" style="--c: #f59e0b"><span class="sw" /></span><span>Uniques</span>
      </div>
    </div>
  </header>

  <!-- Daily PV line chart -->
  <div class="chart-wrap" v-if="dailyPV.length">
    <svg :viewBox="`0 0 ${vw} ${vh}`" class="chart">
      <line :x1="pad" :y1="vh-pad" :x2="vw-pad" :y2="vh-pad" class="axis"/>
      <line :x1="pad" :y1="pad" :x2="pad" :y2="vh-pad" class="axis"/>

      <!-- PV line -->
      <polyline :points="asPointString(pvSeries.pv)" class="line" style="stroke:#0ea5e9" fill="none" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
      <!-- UU line -->
      <polyline :points="asPointString(pvSeries.uu)" class="line" style="stroke:#f59e0b" fill="none" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>

      <!-- x ticks -->
      <g v-for="(t,i) in pvXTicks" :key="'pvx'+i">
        <text :x="t.x" :y="vh - pad + 16" class="tick" text-anchor="middle">{{ t.label }}</text>
      </g>
    </svg>
  </div>

  <!-- Top Routes / Top Paths -->
  <div class="eng-grid">
    <div>
      <h3 class="tabh">Top routes ({{ topDays }}d)</h3>
      <table class="mini">
        <thead><tr><th>Route</th><th class="num">Views</th></tr></thead>
        <tbody>
          <tr v-for="r in topRoutes" :key="r.route">
            <td>{{ r.route }}</td>
            <td class="num">{{ r.count.toLocaleString() }}</td>
          </tr>
          <tr v-if="!topRoutes.length"><td colspan="2" class="empty">No data</td></tr>
        </tbody>
      </table>
    </div>
    <div>
      <h3 class="tabh">Top paths ({{ topDays }}d)</h3>
      <table class="mini">
        <thead><tr><th>From</th><th>To</th><th class="num">Count</th></tr></thead>
        <tbody>
          <tr v-for="p in topPaths" :key="p.from + '→' + p.to">
            <td>{{ p.from }}</td>
            <td>{{ p.to }}</td>
            <td class="num">{{ p.count.toLocaleString() }}</td>
          </tr>
          <tr v-if="!topPaths.length"><td colspan="3" class="empty">No data</td></tr>
        </tbody>
      </table>
    </div>
  </div>
</section>
<!-- Key actions (button presses etc.) -->
<section class="card">
  <header class="card__head">
    <h2>Key actions</h2>
    <div class="controls">
      <label>
  <input type="checkbox" v-model="includeStaff" @change="fetchEngagement" />
  Include staff traffic
</label>

      <label>
        Event:
        <select v-model="selectedEvent" @change="fetchActionData">
          <option v-for="e in topEvents" :key="e.event" :value="e.event">{{ e.event }}</option>
          <option value="__custom__">Custom…</option>
        </select>
      </label>
      <input
        v-if="selectedEvent === '__custom__'"
        v-model.trim="customEvent"
        class="input"
        placeholder="event_name (e.g., add_credits_click)"
        @keyup.enter="fetchActionData"
        style="min-width:220px"
      />
      <label>
        Range:
        <select v-model.number="evDays" @change="fetchActionData">
          <option :value="7">7 days</option>
          <option :value="30">30 days</option>
          <option :value="90">90 days</option>
        </select>
      </label>
      <div class="legend">
        <span class="lg" style="--c:#22c55e"><span class="sw"></span></span><span>Event count</span>
      </div>
    </div>
  </header>

  <!-- Daily event chart -->
  <div class="chart-wrap" v-if="eventDaily.length">
    <svg :viewBox="`0 0 ${vw} ${vh}`" class="chart">
      <line :x1="pad" :y1="vh-pad" :x2="vw-pad" :y2="vh-pad" class="axis"/>
      <line :x1="pad" :y1="pad" :x2="pad" :y2="vh-pad" class="axis"/>
      <polyline
        :points="asPointString(eventSeries.points)"
        class="line"
        style="stroke:#22c55e"
        fill="none" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"
      />
      <g v-for="(t,i) in eventXTicks" :key="'evx'+i">
        <text :x="t.x" :y="vh - pad + 16" class="tick" text-anchor="middle">{{ t.label }}</text>
      </g>
      <g v-for="(val, i) in eventYTicks" :key="'evy'+i">
        <text :x="pad - 6" :y="val.y" class="tick" text-anchor="end" dominant-baseline="middle">
          {{ val.label }}
        </text>
      </g>
    </svg>
  </div>
  <p v-else class="muted">No events recorded for this window.</p>

  <div class="eng-grid" style="margin-top:.6rem">
    <div>
      <h3 class="tabh">Top events ({{ topDays }}d)</h3>
      <table class="mini">
        <thead><tr><th>Event</th><th class="num">Count</th></tr></thead>
        <tbody>
          <tr v-for="e in topEvents" :key="e.event">
            <td>{{ e.event }}</td>
            <td class="num">{{ e.count.toLocaleString() }}</td>
          </tr>
          <tr v-if="!topEvents.length"><td colspan="2" class="empty">No data</td></tr>
        </tbody>
      </table>
    </div>
    <div>
      <h3 class="tabh">Next pages after “{{ currentEventLabel }}” ({{ evDays }}d)</h3>
      <table class="mini">
        <thead><tr><th>Next route</th><th class="num">Count</th></tr></thead>
        <tbody>
          <tr v-for="r in eventNextPages" :key="r.next_route || 'unknown'">
            <td>{{ r.next_route || '(unknown)' }}</td>
            <td class="num">{{ r.count.toLocaleString() }}</td>
          </tr>
          <tr v-if="!eventNextPages.length"><td colspan="2" class="empty">No data</td></tr>
        </tbody>
      </table>
    </div>
  </div>
</section>

  </main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminSignupsDashboard',
  data() {
    return {
      // ranges  evDays: 30,
    selectedEvent: 'add_credits_click', // default pick (change if you want)
    customEvent: '',
    includeStaff: false, // default: exclude staff
    eventDaily: [],        // [{date, count}]
    topEvents: [],         // [{event, count}]
    eventNextPages: [],     // [{next_route, count}]
      days: 30,
      months: 12,
  pvDays: 30,
    topDays: 7,
    dailyPV: [],        // [{date, pageviews, uniques}]
    topRoutes: [],      // [{route, count}]
    topPaths: [],        // [{from, to, count}]
      // state
      loadingDaily: false,
      loadingMonthly: false,

      // raw
      totals: { pending: 0, approved: 0, rejected: 0 },
      metrics: { range_approved: 0, range_rejected: 0, approval_rate_pct: 0 },
      dailyRange: { start: '', end: '', days: 0 },
      dailyData: { approved: [], rejected: [], pending: [] },
      monthlyData: { approved: [], rejected: [], pending: [] },

      // chart dims
      vw: 820, vh: 260, pad: 36,

      // derived series
      dailySeries: {
        approved: { points: [] },
        rejected: { points: [] },
        pending:  { points: [] }
      },

      // monthly bars derived
      monthlyBuckets: [],
    };
  },
watch: {
  topDays() { this.fetchTopEvents(); },
  includeStaff() {
    // refetch both Engagement and Key Actions when toggled
    this.fetchEngagement();
    this.fetchTopEvents().then(() => this.fetchActionData());
  }
},
  computed: {
    // daily axes ticks
    yTicks() { return this.makeGridLines(5).map(v => v.y); },
    yTickVals() { return this.makeGridLines(5); },

    // monthly axes ticks
    yTicksMonthly() { return this.makeGridLinesMonthly(5).map(v => v.y); },
    yTickValsMonthly() { return this.makeGridLinesMonthly(5); },

    // bar sizing
    barGroupWidth() { return 30; },
    barWidth() { return 8; },

      pvSeries() {
    // build simple line points for pageviews + uniques
    const left=this.pad,right=this.vw-this.pad,top=this.pad,bottom=this.vh-this.pad;
    const w=right-left,h=bottom-top;
    const n=Math.max(1, this.dailyPV.length-1);
    const maxVal=Math.max(...this.dailyPV.map(d=>Math.max(d.pageviews,d.uniques,1)),1);
    const mk = (arr, key) => arr.map((x,i)=>{
      const t=n===0?0:i/n; const xPos=left+t*w; const yPos=bottom-(x[key]/maxVal)*h; return [xPos,yPos];
    });
    return {
      pv: mk(this.dailyPV,'pageviews'),
      uu: mk(this.dailyPV,'uniques'),
      maxVal
    };
  },
  pvXTicks() {
    const left=this.pad,right=this.vw-this.pad,w=right-left;
    const labels=this.dailyPV.map(d=>d.date);
    const n=Math.max(1,labels.length-1);
    const every=Math.ceil(Math.max(1,labels.length)/6);
    return labels.map((lab,i)=>{
      if(i%every!==0 && i!==labels.length-1) return null;
      const t=n===0?0:i/n; const x=left+t*w;
      return { x, label: lab.slice(5) };
    }).filter(Boolean);
  },
   currentEvent() {
    return this.selectedEvent === '__custom__'
      ? (this.customEvent || '').trim()
      : this.selectedEvent;
  },
  currentEventLabel() {
    return this.currentEvent || '(choose an event)';
  },
  eventSeries() {
    const left=this.pad,right=this.vw-this.pad,top=this.pad,bottom=this.vh-this.pad;
    const w=right-left,h=bottom-top;
    const n=Math.max(1, this.eventDaily.length-1);
    const maxVal=Math.max(...this.eventDaily.map(d=>d.count), 1);
    const pts = this.eventDaily.map((x,i)=>{
      const t=n===0?0:i/n; const xPos=left+t*w; const yPos=bottom-(x.count/maxVal)*h; return [xPos,yPos];
    });
    return { points: pts, maxVal };
  },
  eventXTicks() {
    const left=this.pad,right=this.vw-this.pad,w=right-left;
    const labels=this.eventDaily.map(d=>d.date);
    const n=Math.max(1,labels.length-1);
    const every=Math.ceil(Math.max(1,labels.length)/6);
    return labels.map((lab,i)=>{
      if(i%every!==0 && i!==labels.length-1) return null;
      const t=n===0?0:i/n; const x=left+t*w;
      return { x, label: lab.slice(5) };
    }).filter(Boolean);
  },
  eventYTicks() {
    const bottom=this.vh-this.pad, top=this.pad, h=bottom-top;
    const maxVal=Math.max(this.eventSeries.maxVal,1);
    const step=this.niceStep(maxVal,5);
    const out=[];
    for(let v=0; v<=maxVal; v+=step){
      const y=bottom-(v/Math.max(1,maxVal))*h; out.push({ y, label:v });
    }
    return out;
  },
    // AFTER (inside computed)
xTicksDaily() {
  const left = this.pad, right = this.vw - this.pad, w = right - left;
  const labels = (this.dailyData?.approved ?? []).map(d => d.date);
  const n = Math.max(1, labels.length - 1);
  const every = Math.ceil(Math.max(1, labels.length) / 6);
  return labels.map((lab, i) => {
    if (i % every !== 0 && i !== labels.length - 1) return null;
    const t = n === 0 ? 0 : i / n;
    const x = left + t * w;
    return { x, label: lab.slice(5) }; // MM-DD
  }).filter(Boolean);
},
  },
  methods: {
    staffParam() { return { include_staff: this.includeStaff ? 1 : 0 }; },
   async fetchEngagement() {
  const headers = this.authHeader();
  const sp = this.staffParam();
  const a = axios.get('/etherscan/fetch_pageviews_daily.php', {
    headers, params: { days: this.pvDays, ...sp }
  });
  const b = axios.get('/etherscan/fetch_top_routes.php', {
    headers, params: { days: this.topDays, limit: 10, ...sp }
  });
  const c = axios.get('/etherscan/fetch_top_paths.php', {
    headers, params: { days: this.topDays, limit: 10, ...sp }
  });
  const [r1,r2,r3] = await Promise.all([a,b,c]);
  if (r1.data?.status==='success') this.dailyPV = r1.data.daily || [];
  if (r2.data?.status==='success') this.topRoutes = r2.data.top_routes || [];
  if (r3.data?.status==='success') this.topPaths = r3.data.top_paths || [];
},

    authHeader() {
      const token = localStorage.getItem('token') || '';
      return { Authorization: `Bearer ${token}` };
    },
 
  async fetchTopEvents() {
  const headers = this.authHeader();
  const sp = this.staffParam();
  const r = await axios.get('/etherscan/fetch_top_events.php', {
    headers, params: { days: this.topDays, limit: 20, ...sp }
  });
  if (r.data?.status === 'success') {
    this.topEvents = r.data.top_events || [];
    if (this.selectedEvent !== '__custom__' && !this.topEvents.find(e => e.event === this.selectedEvent)) {
      this.selectedEvent = this.topEvents[0]?.event || '__custom__';
    }
  }
},

async fetchActionData() {
  if (!this.currentEvent) { this.eventDaily = []; this.eventNextPages = []; return; }
  const headers = this.authHeader();
  const sp = this.staffParam();
  const [d, n] = await Promise.all([
    axios.get('/etherscan/fetch_events_daily.php', {
      headers, params: { event: this.currentEvent, days: this.evDays, ...sp }
    }),
    axios.get('/etherscan/fetch_event_to_page.php', {
      headers, params: { event: this.currentEvent, days: this.evDays, limit: 10, ...sp }
    })
  ]);
  if (d.data?.status === 'success') this.eventDaily = d.data.daily || [];
  if (n.data?.status === 'success') this.eventNextPages = n.data.next_routes || [];
},

    async fetchDaily() {
      this.loadingDaily = true;
      try {
        const res = await axios.get('/etherscan/fetch_signup_overview_daily.php', {
          headers: this.authHeader(),
          params: { days: this.days }
        });
        if (res.data.status !== 'success') throw new Error(res.data.message || 'Failed daily fetch');
        const d = res.data;

        this.totals = d.totals || this.totals;
        this.metrics = d.metrics || this.metrics;
        this.dailyRange = d.range || this.dailyRange;

        // Normalize to a continuous date array with zeros filled
        const allDays = this.enumerateDays(d.range.start, d.range.end);
        const approved = this.mergeCounts(allDays, d.daily.approved);
        const rejected = this.mergeCounts(allDays, d.daily.rejected);
        const pending  = this.mergeCounts(allDays, d.daily.pending);

        this.dailyData = { approved, rejected, pending };
        this.dailySeries = this.buildDailySeries(allDays, approved, rejected, pending);
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingDaily = false;
      }
    },

    async fetchMonthly() {
      this.loadingMonthly = true;
      try {
        const res = await axios.get('/etherscan/fetch_signup_overview_monthly.php', {
          headers: this.authHeader(),
          params: { months: this.months }
        });
        if (res.data.status !== 'success') throw new Error(res.data.message || 'Failed monthly fetch');
        const m = res.data.monthly || {};

        // Normalize months (YYYY-MM) to continuous buckets
        const monthsList = this.enumerateMonths(res.data.range.start_month, this.months);
        const approved = this.mergeCounts(monthsList, m.approved, 'month');
        const rejected = this.mergeCounts(monthsList, m.rejected, 'month');
        const pending  = this.mergeCounts(monthsList, m.pending,  'month');

        this.monthlyData = { approved, rejected, pending };
        this.monthlyBuckets = this.buildMonthlyBuckets(monthsList, approved, rejected, pending);
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingMonthly = false;
      }
    },

    // --- helpers: normalization
    enumerateDays(startIso, endIso) {
      const out = [];
      const cur = new Date(startIso + 'T00:00:00');
      const end = new Date(endIso + 'T00:00:00');
      while (cur <= end) {
        out.push(cur.toISOString().slice(0,10));
        cur.setDate(cur.getDate() + 1);
      }
      return out;
    },
    enumerateMonths(startYm, count) {
      // startYm: 'YYYY-MM'
      const parts = startYm.split('-').map(n=>parseInt(n,10));
      let y = parts[0], m = parts[1];
      const out = [];
      for (let i=0;i<count;i++) {
        const ym = `${y}-${String(m).padStart(2,'0')}`;
        out.push(ym);
        m += 1;
        if (m > 12) { m = 1; y += 1; }
      }
      return out;
    },
    mergeCounts(keys, rows, keyField='date') {
      const map = new Map();
      (rows||[]).forEach(r => map.set(r[keyField], r.count));
      return keys.map(k => ({ [keyField]: k, count: map.get(k) || 0 }));
    },

    // --- helpers: charts
    buildDailySeries(allDays, approved, rejected, pending) {
      const maxVal = Math.max(
        ...approved.map(x=>x.count),
        ...rejected.map(x=>x.count),
        ...pending .map(x=>x.count),
        1
      );
      const left = this.pad, right = this.vw - this.pad, top = this.pad, bottom = this.vh - this.pad;
      const w = right - left, h = bottom - top;
      const n = Math.max(1, allDays.length - 1);

      function mkPoints(series) {
        return series.map((x, i) => {
          const t = n === 0 ? 0 : i / n;
          const xPos = left + t * w;
          const yPos = bottom - (x.count / maxVal) * h;
          return [xPos, yPos];
        });
      }
      return {
        approved: { points: mkPoints(approved) },
        rejected: { points: mkPoints(rejected) },
        pending:  { points: mkPoints(pending)  }
      };
    },

    makeGridLines(n=5) {
      // daily y grid, based on max daily across series
      const maxDaily = Math.max(
        ...this.dailyData.approved.map(d=>d.count),
        ...this.dailyData.rejected.map(d=>d.count),
        ...this.dailyData.pending .map(d=>d.count),
        1
      );
      const step = this.niceStep(maxDaily, n);
      const ticks = [];
      const bottom = this.vh - this.pad, top = this.pad, h = bottom - top;
      for (let v=0; v<=maxDaily; v+=step) {
        const y = bottom - (v / Math.max(1,maxDaily)) * h;
        ticks.push({ y, label: v });
      }
      return ticks;
    },
    makeGridLinesMonthly(n=5) {
      const maxMonthly = Math.max(
        ...this.monthlyData.approved.map(d=>d.count),
        ...this.monthlyData.rejected.map(d=>d.count),
        ...this.monthlyData.pending .map(d=>d.count),
        1
      );
      const step = this.niceStep(maxMonthly, n);
      const ticks = [];
      const bottom = this.vh - this.pad, top = this.pad, h = bottom - top;
      for (let v=0; v<=maxMonthly; v+=step) {
        const y = bottom - (v / Math.max(1,maxMonthly)) * h;
        ticks.push({ y, label: v });
      }
      return ticks;
    },
    niceStep(maxVal, n) {
      // simple "nice" tick step
      const rough = maxVal / n;
      if (rough <= 1) return 1;
      if (rough <= 2) return 2;
      if (rough <= 5) return 5;
      const pow10 = Math.pow(10, Math.floor(Math.log10(rough)));
      const base = rough / pow10;
      if (base <= 1.5) return 1 * pow10;
      if (base <= 3)   return 2 * pow10;
      if (base <= 7)   return 5 * pow10;
      return 10 * pow10;
    },
    asPointString(pts) { return pts.map(p => `${p[0]},${p[1]}`).join(' '); },

    buildMonthlyBuckets(monthsList, approved, rejected, pending) {
      const left = this.pad, right = this.vw - this.pad, top = this.pad, bottom = this.vh - this.pad;
      const w = right - left, h = bottom - top;
      const n = Math.max(1, monthsList.length - 1);

      const maxVal = Math.max(
        ...approved.map(d=>d.count),
        ...rejected.map(d=>d.count),
        ...pending .map(d=>d.count),
        1
      );

      return monthsList.map((ym, i) => {
        const t = n === 0 ? 0 : i / n;
        const x = left + t * w;
        // heights
        const ap = approved[i]?.count || 0;
        const rj = rejected[i]?.count || 0;
        const pd = pending [i]?.count || 0;
        const yApproved = bottom - (ap / maxVal) * h;
        const yRejected = bottom - (rj / maxVal) * h;
        const yPending  = bottom - (pd / maxVal) * h;
        const hApproved = bottom - yApproved;
        const hRejected = bottom - yRejected;
        const hPending  = bottom - yPending;

        // 3 bars: pending, approved, rejected
        const bars = [
          { key:'pending',  cls:'bar bar--pending',  y:yPending,  h:hPending,  offset: -this.barGroupWidth/2 },
          { key:'approved', cls:'bar bar--approved', y:yApproved, h:hApproved, offset: -this.barWidth/2       },
          { key:'rejected', cls:'bar bar--rejected', y:yRejected, h:hRejected, offset:  this.barGroupWidth/2 - this.barWidth }
        ];

        return {
          x,
          label: ym, // YYYY-MM
          bars
        };
      });
    }
  },

  mounted() {
    console.log("beginning fetch");
    // router guard will manage access; here we just load
    this.fetchDaily();
    this.fetchMonthly();
  this.fetchEngagement(); // NEW
  this.fetchTopEvents().then(() => this.fetchActionData());
  }
};
</script>

<style scoped>
.signups-dashboard { max-width: 1120px; margin: 0 auto; padding: 1rem; color:#1f2a37; }
.page-head h1 { margin: 0; font-size: 1.5rem; font-weight: 800; color:#0f2740; }
.sub { color:#667085; margin:.15rem 0 .8rem; }
.admin-note { font-size:.8rem; color:#94a3b8; }

/* KPIs */
.grid.kpis {
  display:grid; gap:.8rem;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: .9rem;
}
.kpi {
  background:#fff; border:1px solid #e5e7eb; border-radius:14px;
  box-shadow:0 10px 20px rgba(16,24,40,.06);
  padding:.9rem;
}
.kpi h3 { margin:0; font-size:.9rem; color:#0f2740; }
.kpi__value { font-size:1.6rem; font-weight:800; margin:.2rem 0 .1rem; }
.kpi__hint { color:#6b7280; margin:0; font-size:.86rem; }

/* Card */
.card {
  background:#fff; border:1px solid #e5e7eb; border-radius:14px;
  box-shadow:0 10px 20px rgba(16,24,40,.06);
  padding:1rem; margin-bottom:1rem;
}
.card__head { display:flex; justify-content:space-between; align-items:center; margin-bottom:.6rem; }

/* Controls & legend */
.controls { display:flex; gap:.6rem; align-items:center; flex-wrap:wrap; }
.controls label { color:#475467; font-weight:600; }
.controls select { margin-left:.35rem; border:1px solid #e5e7eb; border-radius:8px; padding:.25rem .45rem; }
.legend { display:flex; gap:.5rem; align-items:center; font-weight:700; }
.lg { display:inline-flex; align-items:center; gap:.35rem; font-size:.82rem; color:#334155; }
.lg::before {
  content:''; width:12px; height:12px; border-radius:4px; display:inline-block;
}
.lg--approved::before { background:#2563eb; }
.lg--rejected::before { background:#ef4444; }
.lg--pending::before  { background:#10b981; }

/* Chart */
.chart-wrap { overflow-x:auto; }
.chart { width:100%; height:auto; display:block; }
.axis { stroke:#cbd5e1; stroke-width:1; }
.grid { stroke:#e5e7eb; stroke-width:1; }
.tick { fill:#6b7280; font-size:.78rem; }

/* Lines */
.line--approved { stroke:#2563eb; }
.line--rejected { stroke:#ef4444; }
.line--pending  { stroke:#10b981; }

/* Bars */
.bar { fill:#cbd5e1; }
.bar--approved { fill:#2563eb; }
.bar--rejected { fill:#ef4444; }
.bar--pending  { fill:#10b981; }

/* Skeleton */
.skeleton { display:grid; gap:.5rem; }
.skeleton-row { height:42px; background:linear-gradient(90deg,#f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%); background-size:400% 100%; animation:shimmer 1.2s infinite; border-radius:10px; }
@keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:0 0} }

@media (max-width: 980px) {
  .grid.kpis { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .grid.kpis { grid-template-columns: 1fr; }
}

.legend { display:flex; gap:.6rem; align-items:center; }
.legend .lg { display:inline-flex; align-items:center; gap:.35rem; font-weight:700; color:#334155; }
.legend .sw { width:12px; height:12px; border-radius:4px; background: var(--c); display:inline-block; }

.eng-grid { display:grid; grid-template-columns: 1fr 1fr; gap:.8rem; margin-top:.8rem; }
.min i, .mini { width:100%; border-collapse:separate; border-spacing:0; }
.mini th, .mini td { padding:.45rem .5rem; border-bottom:1px solid #eef1f5; }
.mini th { text-align:left; font-weight:700; color:#0f2740; background:#f8fafc; }
.mini .num { text-align:right; }
.tabh { margin:.4rem 0; font-size:.98rem; color:#0f2740; }
@media (max-width: 760px){ .eng-grid { grid-template-columns: 1fr; } }

</style>
